/**
 * Перелік розділів системних даних платформи — спільне джерело для лівого
 * меню (PlatformLayout) і сторінки (PlatformSystemDataPage).
 *
 * Згруповано за темами для зручної навігації у sidebar.
 */

export type PlatformSectionId =
  | 'currencies'
  | 'vat-rates'
  | 'banks'
  | 'tax-constants'
  | 'classifiers'
  | 'uoms'
  | 'calendar'
  | 'income-types'
  | 'dismissal-grounds'
  | 'inflation'
  | 'worktime'
  | 'disability'
  | 'tax-list'
  | 'tax-purposes'
  | 'cash-flow-items'
  | 'taxation-schemes'
  | 'tax-offices'
  | 'asset-groups'
  | 'depreciation-methods'
  | 'dps-certs'
  | 'ai-credits'
  | 'billing'
  | 'migration-1c'
  | 'processing-waitlist';

export interface PlatformSection {
  id: PlatformSectionId;
  label: string;
}

export interface PlatformSectionGroup {
  title: string;
  sections: PlatformSection[];
}

export const PLATFORM_SECTION_GROUPS: PlatformSectionGroup[] = [
  {
    title: 'Фінансові',
    sections: [
      { id: 'currencies', label: 'Валюти' },
      { id: 'vat-rates', label: 'Ставки ПДВ' },
      { id: 'banks', label: 'Банки' },
      { id: 'tax-list', label: 'Перелік податків' },
      { id: 'cash-flow-items', label: 'Статті руху коштів (ДДС)' },
    ],
  },
  {
    title: 'Податки та облік',
    sections: [
      { id: 'tax-constants', label: 'Податкові константи' },
      { id: 'dps-certs', label: 'Сертифікати ДПС' },
      { id: 'ai-credits', label: 'AI-кредити (Oblik+/AI)' },
      { id: 'billing', label: 'Баланс і тарифи клієнтів' },
      { id: 'classifiers', label: 'Класифікатори' },
      { id: 'uoms', label: 'Одиниці виміру (ДК 011-96)' },
      { id: 'income-types', label: 'Ознаки доходів (4ДФ)' },
      { id: 'inflation', label: 'Індекс інфляції' },
      { id: 'tax-purposes', label: 'Податкові призначення' },
      { id: 'taxation-schemes', label: 'Схеми оподаткування' },
      { id: 'tax-offices', label: 'Податкові інспекції (ДПІ)' },
    ],
  },
  {
    title: 'Перенесення з 1С',
    sections: [
      { id: 'migration-1c', label: 'Обробка 1С' },
      { id: 'processing-waitlist', label: 'Перелік очікувачів' },
    ],
  },
  {
    title: 'Необоротні активи',
    sections: [
      { id: 'asset-groups', label: 'Групи активів (ПКУ 138.3)' },
      { id: 'depreciation-methods', label: 'Методи амортизації' },
    ],
  },
  {
    title: 'Кадри та час',
    sections: [
      { id: 'calendar', label: 'Виробничий календар' },
      { id: 'worktime', label: 'Коди робочого часу (П-5)' },
      { id: 'disability', label: 'Причини непрацездатності' },
      { id: 'dismissal-grounds', label: 'Підстави звільнення' },
    ],
  },
];

// Плоский список (для валідації/пошуку).
export const PLATFORM_SECTIONS: PlatformSection[] =
  PLATFORM_SECTION_GROUPS.flatMap((g) => g.sections);

export const DEFAULT_PLATFORM_SECTION: PlatformSectionId = 'currencies';

export function isPlatformSection(v: string | null): v is PlatformSectionId {
  return PLATFORM_SECTIONS.some((s) => s.id === v);
}
